/** 一期 P0 mock —— 对齐 `02_UDHUB云枢_一期PRD_v1.0.md` */

/**
 * 项目生命周期单一状态：
 * 调试中 →（移交）→ 已交付 →（申请授权）→ 待授权 →（客户授权）→ 已授权
 * 已授权 →（维护结束 / 客户授权结束）→ 已交付
 * 不支持「授权暂停」
 */
export type DeliveryStatus =
  | "待激活"
  | "待认领"
  | "调试中"
  /** @deprecated 等同调试中 */
  | "未交付"
  | "已交付"
  | "待授权"
  | "已授权"
  /** @deprecated 已废弃，归一为已交付 */
  | "授权暂停";

/** 移交前：安装调试期，无需业主授权即可写操作 */
export function isPreHandoff(status: DeliveryStatus): boolean {
  return (
    status === "调试中" ||
    status === "未交付" ||
    status === "待激活" ||
    status === "待认领"
  );
}

/** 是否已移交（进入售后） */
export function isHandedOver(status: DeliveryStatus): boolean {
  return !isPreHandoff(status);
}

/** 归一化展示用状态（兼容旧值） */
export function normalizeDeliveryStatus(
  status: DeliveryStatus,
): DeliveryStatus {
  if (status === "未交付") return "调试中";
  if (status === "授权暂停") return "已交付";
  return status;
}

/** 是否允许远程写操作（试操作 / 改配置） */
export function canRemoteWrite(
  project: Pick<Project, "deliveryStatus" | "auth">,
  need: "维护" | "配置" = "维护",
): boolean {
  if (isPreHandoff(project.deliveryStatus)) return true;
  const status = normalizeDeliveryStatus(project.deliveryStatus);
  if (status !== "已授权") return false;
  if (project.auth.status !== "有效") return false;
  if (need === "配置") return project.auth.scopes.includes("配置");
  return (
    project.auth.scopes.includes("维护") ||
    project.auth.scopes.includes("配置")
  );
}

export type OnlineStatus = "online" | "offline" | "warn";

export type AuthScope = "监控" | "维护" | "配置";

/** Agent / 接入能力协商（对齐 07_平台形态借鉴） */
export type CapabilityLevel =
  | "supported"
  | "limited"
  | "experimental"
  | "blocked";

export type CapabilityKey =
  | "heartbeat"
  | "coreVersion"
  | "osVersion"
  | "supervisorVersion"
  | "hostMetrics"
  | "deviceControl"
  | "registryWrite";

export interface CapabilityItem {
  key: CapabilityKey;
  label: string;
  level: CapabilityLevel;
  note?: string;
}

export interface CustomerMaster {
  email?: string;
  buildingType?: string;
  yearBuilt?: string;
  rooms?: string;
  floors?: string;
  networkTopology?: string;
  ups?: string;
  isp?: string;
  installDate?: string;
  hardwareAssetId?: string;
}

export interface Project {
  id: string;
  name: string;
  customer: string;
  address: string;
  city: string;
  contact: string;
  phone: string;
  deliveryStatus: DeliveryStatus;
  /** 项目分类：not_delivered | pending_support | delivered */
  category: "not_delivered" | "pending_support" | "delivered";
  /** 接入通道：ha | vendor | hybrid（缺省按 ha） */
  accessChannel?: "ha" | "vendor" | "hybrid";
  /** 厂家项目展示名（可选） */
  vendorLabel?: string | null;
  deliveredAt?: string;
  deviceCount: number;
  onlineRate: number;
  openAlerts: number;
  openTickets: number;
  hardware: string;
  installForm: "HAOS" | "Container" | "Supervised";
  supportLevel: "正式支持" | "实验支持";
  core: string;
  os: string;
  supervisor: string;
  agent: string;
  agentStatus: "已连接" | "已断开" | "未激活" | "未绑定主机";
  lastPing: string;
  /** SSH 诊断旁路状态（一期） */
  sshBypassStatus?: "unsupported" | "missing" | "stopped" | "enabled" | "unknown" | string;
  sshBypassDetail?: string | null;
  cpu?: number;
  ramUsed?: number;
  ramTotal?: number;
  disk?: number;
  memoryPercent?: number;
  hostMetricsSupported?: boolean;
  hostMetricsSource?: string;
  hostMetricsUpdatedAt?: string;
  capabilities: CapabilityItem[];
  auth: {
    scopes: AuthScope[];
    expires: string;
    status: "有效" | "未授权" | "已暂停" | "已过期";
  };
  gatewayId: string;
  master?: CustomerMaster;
  errorCount?: number;
  warningCount?: number;
  /** Fleet 对齐：Agent 接入密钥 */
  agentKey?: string;
  agentKeyStatus?: "active" | "revoked";
  /** 设备规模预估（认领/交付前） */
  deviceScaleEstimate?: number;
  /** 待认领激活码展示 */
  claimCode?: string;
  /** 租户归属（平台管理员可见） */
  tenantId?: string;
  tenantName?: string;
}

export function capabilitiesForForm(
  form: Project["installForm"],
): CapabilityItem[] {
  if (form === "Container") {
    return [
      { key: "heartbeat", label: "在线心跳", level: "supported" },
      { key: "coreVersion", label: "Core / Agent 版本", level: "supported" },
      {
        key: "osVersion",
        label: "HA OS 版本",
        level: "blocked",
        note: "Container 无 Supervisor/OS 管理面",
      },
      {
        key: "supervisorVersion",
        label: "Supervisor 版本",
        level: "blocked",
        note: "不适用",
      },
      {
        key: "hostMetrics",
        label: "CPU / 内存 / 磁盘",
        level: "limited",
        note: "依赖宿主机暴露；可能不完整",
      },
      { key: "deviceControl", label: "受控设备操作", level: "supported" },
    ];
  }
  if (form === "Supervised") {
    return [
      {
        key: "heartbeat",
        label: "在线心跳",
        level: "experimental",
        note: "实验支持，明示风险",
      },
      {
        key: "coreVersion",
        label: "Core / Agent 版本",
        level: "experimental",
      },
      { key: "osVersion", label: "HA OS 版本", level: "limited" },
      { key: "supervisorVersion", label: "Supervisor 版本", level: "limited" },
      {
        key: "hostMetrics",
        label: "CPU / 内存 / 磁盘",
        level: "experimental",
      },
      {
        key: "deviceControl",
        label: "受控设备操作",
        level: "experimental",
      },
    ];
  }
  return [
    { key: "heartbeat", label: "在线心跳", level: "supported" },
    { key: "coreVersion", label: "Core / Agent 版本", level: "supported" },
    { key: "osVersion", label: "HA OS 版本", level: "supported" },
    { key: "supervisorVersion", label: "Supervisor 版本", level: "supported" },
    { key: "hostMetrics", label: "CPU / 内存 / 磁盘", level: "supported" },
    { key: "deviceControl", label: "受控设备操作", level: "supported" },
  ];
}

export function cap(
  project: Project,
  key: CapabilityKey,
): CapabilityItem | undefined {
  return project.capabilities.find((c) => c.key === key);
}

export function formatMetric(
  project: Project,
  key: CapabilityKey,
  value: string | undefined,
): string {
  const c = cap(project, key);
  if (!c || c.level === "blocked") return "不支持";
  if (value == null || value === "") return "—";
  return value;
}

export interface Device {
  id: string;
  projectId: string;
  name: string;
  room: string;
  domain: string;
  entityId: string;
  state: string;
  online: boolean;
  lastSeen: string;
  attributes: Record<string, string | number | boolean>;
  manufacturer?: string;
  model?: string;
  deviceId?: string;
  gatewayId?: string;
  source?: string;
  /** Grouped HA entities (功能项) when list is device-centric */
  entities?: Device[];
  primaryEntityId?: string;
  entityCount?: number;
  areaId?: string;
  /** 云枢品类手动覆盖；空则按 domain 推断 */
  deviceCategory?: string | null;
  /** HA 2026.9 child → parent external device id */
  parentDeviceId?: string | null;
  viaDeviceId?: string | null;
  configEntryId?: string | null;
  swVersion?: string | null;
  isChild?: boolean;
  /**
   * HA DeviceEntryType。service =「已集成」详情里 Services 分区
   *（非开发者工具 call_service 列表）。
   */
  entryType?: "device" | "service";
}

export interface Scene {
  id: string;
  projectId: string;
  name: string;
  managedBy: "HA" | "云枢托管" | "验证中";
  entities: number;
  lowRisk: boolean;
  entityId?: string;
  channel?: "runtime" | "managed" | "ha";
  /** Known target entity ids when available */
  entityRefs?: string[];
}

export interface Automation {
  id: string;
  projectId: string;
  name: string;
  enabled: boolean;
  lastResult: "成功" | "失败" | "未执行";
  lastRun: string;
  failSummary?: string;
  entityId?: string;
  entityRefs?: string[];
  channel?: "managed" | "runtime" | "ha";
  managedBy?: string;
  /** 运营可见的 AI 温控/最小因子自动化 */
  aiManaged?: boolean;
}

export interface HaScript {
  id: string;
  projectId: string;
  name: string;
  entityId: string;
  enabled: boolean;
  lastRun?: string;
  channel?: "managed" | "ha";
  managedBy?: string;
}

export interface AlertItem {
  id: string;
  projectId: string;
  projectName: string;
  level: "提示" | "一般" | "严重";
  rule: string;
  target: string;
  status: "活动" | "已恢复" | "已静默" | "已处理";
  createdAt: string;
  notified: string;
  ticketId?: string;
  signature?: string;
}

export interface AlertRule {
  id: string;
  name: string;
  level: "提示" | "一般" | "严重";
  trigger: string; // e.g. 心跳丢失 > 5 分钟
  channels: string[];
  quietMinutes: number;
  enabled: boolean;
  autoTicket: boolean;
}

export interface TicketEvent {
  at: string;
  actor: string;
  text: string;
}

export interface Ticket {
  id: string;
  projectId: string;
  projectName: string;
  source: "客户报障" | "告警" | "手动";
  title: string;
  priority: "低" | "中" | "高";
  status: "待分派" | "处理中" | "待回访" | "已关闭" | "已取消";
  assignee: string;
  createdAt: string;
  device?: string;
  needVisit: boolean;
  timeline?: TicketEvent[];
  diagnosis?: string;
  serviceNote?: string;
  alertId?: string;
  signature?: string;
  alertCount?: number;
  openAlertCount?: number;
}

export type AuditCategory = string;

export interface AuditEntry {
  id?: string;
  time: string;
  /** ISO 日期，供区间筛选 */
  day: string;
  actor: string;
  action: string;
  target: string;
  result: string;
  category: AuditCategory;
  /** ISO 完整时间，详情页展示 */
  createdAt?: string;
}

/** 免费版工作区默认只启用第一个项目；其余样例保留供详情/告警引用 */
export const freeWorkspaceProjectIds: string[] = ["prj_4604d8022d20"];

export const projects: Project[] = [];

export const devices: Device[] = [];

export const scenes: Scene[] = [];

export const automations: Automation[] = [];

export const alerts: AlertItem[] = [];

export const tickets: Ticket[] = [];

export const alertRules: AlertRule[] = [];

export interface StaffUser {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  role: string;
  enabled: boolean;
  /** 展示文案：项目名列表或「全部项目」 */
  projects: string;
  /** 已分配项目 ID（管理员角色为空且 scope=all） */
  projectIds: string[];
  projectScope?: "all" | "assigned";
  lastActive: string;
}

export const users: StaffUser[] = [];

export const auditLogs: AuditEntry[] = [];

export const releaseNotes = [
  {
    version: "0.1.2-web-p0",
    date: "2026-08-14",
    title: "一期 PRD · Web 能力补齐",
    items: [
      "总览增加告警/工单摘要；告警与工单升入主导航",
      "告警规则 CRUD、静默/恢复、真实转工单",
      "工单状态机闭环、诊断快照、服务记录与时间线",
      "认领项目：激活码 / 客户手机号二选一绑定；调试台 Service 二次确认与批量",
      "内置客服角色；用户可配置负责客户范围",
    ],
  },
  {
    version: "0.1.1-parity",
    date: "2026-08-14",
    title: "登录与导航调整",
    items: [
      "主导航命名对齐：客户 / 设备 / 用户；/customers 为规范路由",
      "客户列表补齐 Agent Key：显示 / 轮换 / 吊销",
      "设备表增加绑定客户列；用户表增加负责客户与最近活跃",
    ],
  },
  {
    version: "0.1.0-parity",
    date: "2026-08-13",
    title: "导航与表单完善",
    items: [
      "主导航：工作台 / 项目 / 设备 / 用户 / 审计 / 资源 / 设置",
      "项目新建表单补齐主数据字段与一次性 Agent Key",
      "差异化模块（告警 / 工单 / 计费）移入后置分区",
    ],
  },
  {
    version: "0.0.9",
    date: "2026-08-10",
    title: "设备页恢复",
    items: ["Devices 页恢复为设备 / 硬件 SKU 库", "支持目录导入与自定义设备"],
  },
];

export const billing = {
  plan: "专业版",
  priceHint: "按项目扩容 · 适合多现场集成商",
  projectQuota: 50,
  projectUsed: 0,
  softHostQuota: 50,
  softDeviceQuota: 5000,
  deviceUsed: 0,
};

export const agentInstallSteps = [
  {
    title: "安装云枢 Agent",
    tag: "插件",
    desc: "在兼容的 HAOS / Container 上安装签名发布的 custom integration。",
    steps: [
      "按资源中心指引安装（HACS 自定义仓库或 ZIP）",
      "重启 Home Assistant",
      "设置 → 设备与服务 → 添加「云枢 Agent」",
    ],
  },
  {
    title: "激活并绑定项目",
    tag: "激活",
    desc: "插件绑定客户手机号；云枢用激活码或手机号二选一认领。",
    steps: [
      "HA 安装 Agent，配置向导绑定客户手机号（或显示激活码）",
      "云枢「认领项目」：激活码 / 客户手机号二选一完成绑定",
      "认领成功后项目进入「调试中」",
    ],
  },
  {
    title: "业主授权交付",
    tag: "授权",
    desc: "绑定后为「调试中」；移交后为「已交付」；申请授权→「待授权」；客户授权→「已授权」；授权结束→「已交付」。",
    steps: [
      "生成项目授权二维码 / 链接",
      "业主确认监控 / 维护 / 配置范围与期限",
      "授权写入双方可见审计；撤销 1 分钟内生效",
    ],
  },
];

export function projectOnlineStatus(p: Project): OnlineStatus {
  if (p.agentStatus === "已断开" || p.onlineRate === 0) return "offline";
  if (p.openAlerts > 0 || (p.disk ?? 0) >= 80) return "warn";
  return "online";
}

export interface HardwareAsset {
  id: string;
  name: string;
  vendor: string;
  os: string;
  cpu: string;
  ram: string;
  storage: string;
  note: string;
  imageHint: string;
  fromCatalog: boolean;
  linkedProjectId?: string;
  linkedProjectName?: string;
}

/** 实测 Fleet v1.12.0 的 13 个硬件目录条目。 */
export const hardwareCatalog = [
  { name: "Beelink S12 Pro", vendor: "Beelink", os: "HAOS", cpu: "Intel N100 · 4 核 · 3.4 GHz", ram: "16 GB", storage: "—", imageHint: "beelink", description: "常见的高性价比迷你主机，适合较大规模的安装。" },
  { name: "BMAX B1 Pro", vendor: "BMAX", os: "HAOS", cpu: "Intel Celeron N4020 · 2 核 · 2.8 GHz", ram: "6 GB", storage: "—", imageHint: "bmax", description: "入门级迷你主机，在 HAOS 社区中较常见。" },
  { name: "GMKtec NucBox G3", vendor: "GMKtec", os: "HAOS", cpu: "Intel N100 · 4 核 · 3.4 GHz", ram: "16 GB", storage: "—", imageHint: "gmktec", description: "Beelink S12 Pro 的同级替代机型。" },
  { name: "Home Assistant Blue (ODROID N2+)", vendor: "Nabu Casa", os: "HAOS", cpu: "ARM Cortex-A73 · 4 核 + A53 · 2 核 · 2.4 GHz", ram: "4 GB", storage: "—", imageHint: "blue", description: "基于 ODROID N2+ 的早期官方 HAOS 设备。" },
  { name: "Home Assistant Green", vendor: "Nabu Casa", os: "HAOS", cpu: "ARM Cortex-A55 · 4 核 · 1.8 GHz", ram: "4 GB", storage: "32 GB eMMC", imageHint: "green", description: "官方入门级 HAOS 硬件，预装系统并支持即插即用。" },
  { name: "Home Assistant Yellow (CM4 · 4 GB)", vendor: "Nabu Casa", os: "HAOS", cpu: "ARM Cortex-A72 · 4 核 · 1.5 GHz", ram: "4 GB", storage: "M.2 NVMe", imageHint: "yellow", description: "CM4 官方设备，支持 NVMe、PoE 选配及集成 Zigbee。" },
  { name: "Intel NUC 11 Essential (NUC11ATKC4)", vendor: "Intel", os: "HAOS", cpu: "Intel Celeron N5105 · 4 核 · 2.9 GHz", ram: "8 GB", storage: "M.2 NVMe", imageHint: "nuc", description: "成熟的 x86 HAOS Generic 平台。" },
  { name: "Raspberry Pi 4 Model B · 2 GB", vendor: "Raspberry Pi Ltd.", os: "HAOS", cpu: "ARM Cortex-A72 · 4 核 · 1.8 GHz", ram: "2 GB", storage: "microSD", imageHint: "pi4", description: "适合少量集成的小型 HAOS 安装。" },
  { name: "Raspberry Pi 4 Model B · 4 GB", vendor: "Raspberry Pi Ltd.", os: "HAOS", cpu: "ARM Cortex-A72 · 4 核 · 1.8 GHz", ram: "4 GB", storage: "microSD", imageHint: "pi4", description: "适合典型智能家居安装的主流版本。" },
  { name: "Raspberry Pi 4 Model B · 8 GB", vendor: "Raspberry Pi Ltd.", os: "HAOS", cpu: "ARM Cortex-A72 · 4 核 · 1.8 GHz", ram: "8 GB", storage: "microSD", imageHint: "pi4", description: "适合较多 Add-on 的 Pi 4 高配版本。" },
  { name: "Raspberry Pi 5 · 16 GB", vendor: "Raspberry Pi Ltd.", os: "HAOS", cpu: "ARM Cortex-A76 · 4 核 · 2.4 GHz", ram: "16 GB", storage: "microSD / NVMe", imageHint: "pi5", description: "适合并行容器和数据库的高配版本。" },
  { name: "Raspberry Pi 5 · 4 GB", vendor: "Raspberry Pi Ltd.", os: "HAOS", cpu: "ARM Cortex-A76 · 4 核 · 2.4 GHz", ram: "4 GB", storage: "microSD / NVMe", imageHint: "pi5", description: "当前代入门版本，支持 PCIe 扩展。" },
  { name: "Raspberry Pi 5 · 8 GB", vendor: "Raspberry Pi Ltd.", os: "HAOS", cpu: "ARM Cortex-A76 · 4 核 · 2.4 GHz", ram: "8 GB", storage: "microSD / NVMe", imageHint: "pi5", description: "适合复杂 HAOS 安装的常用版本。" },
];

export const hardwareAssets: HardwareAsset[] = [];
